<template>
  <div
    class="cl-item"
    @click="onPodcastClick(clItemData.uuid)">
    <div class="cl-item-image">
      <picture-component :src="clItemData.display_image_url" />

      <el-progress
        v-if="showProgressBar"
        :percentage="currentProgress"
        :stroke-width="7"
        :show-text="false"
        color="#00AECB"/>
    </div>
    <div class="cl-item-content">
      <div
        class="playable">
        <p
          v-if="!displayTemp"
          data-test="episodetitle">{{ clItemData.title }}</p>
        <p
          v-if="displayTemp"
          data-test="cltag">{{ clItemData.temp_title || 'Resume Episode' }}</p>
      </div>
    </div>
  </div>
</template>
<script>
import * as log from 'loglevel';
import Podcast from '../services/Podcast';
import placeholder from '../assets/images/placeholder.png';
import Episode from '../services/Episode';
import PlaybackService from '../services/PlaybackService';
import PlayerEvent from '../services/PlayerEvent';
import PremiumIndicator from './PremiumIndicator';

export default {
  name: 'CLItem',
  components: {
    PremiumIndicator,
  },
  props: {
    episode: {
      type: Object,
      default: () => {},
    },
    bucket: {
      type: Object,
      default: () => {},
    },
    mediaSize: {
      type: String,
      default: () => 'thumbnail',
    },
    redirect: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isSelected: false,
      clItemData: this.episode,
      placeholder,
      text_limit: 40,
      podcastProvider: new Podcast(),
      episodeProvider: new Episode(),
      playbackService: new PlaybackService(),
      podcast_title: this.$options.filters.truncate(this.episode.title, this.text_limit),
      show_temp_title: true,
      playerEventProvider: new PlayerEvent(),
      prevContext: '',
    };
  },
  computed: {
    showProgressBar() {
      return this.currentProgress && !this.clItemData.all_caught_up && this.clItemData.temp_title !== 'Play Next Episode';
    },
    displayTemp() {
      const { episode, play } = this.$store.getters;
      if (this.clItemData.type === 'play' && episode.uuid === this.clItemData.uuid) {
        return !play;
      }

      return true;
    },
    currentProgress() {
      const progress = this.$store.getters.episodeProgressByUuid(this.episode.uuid);
      if (progress) {
        let pg = progress.progress_percent && progress.progress_percent > 0 ? progress.progress_percent : 1;
        pg = pg > 100 ? 100 : pg;
        return pg;
      }
      return 0;
    },
    playing() {
      return this.$store.getters.play;
    },
    isPremium() {
      return this.episode.is_exclusive;
    },
  },
  watch: {
    episode(newVal) {
      this.clItemData = newVal;
      this.setTempTitle();
    },
    playing(newVal) {
      this.setShowTempTitle(newVal);
    },
    currentProgress(newVal) {
      if (newVal > 95 || newVal < 5) {
        this.setTempTitle();
      }
    },
  },
  mounted() {
    this.clItemData = this.episode;
    if (window.innerWidth <= 567) {
      this.text_limit = 20;
    } else if (window.innerWidth <= 768) {
      this.text_limit = 35;
    }
    this.setTempTitle();
  },
  methods: {
    onPodcastClick() {
      if (!this.redirect) return;
      this.prevContext = this.$store.getters.playlistType;
      if (this.prevContext !== 'continue-listening') {
        this.$store.dispatch('setReloadNext');
      }
      this.$store.dispatch('setPlaylistType', 'continue-listening');
      this.$store.dispatch('setLoadPlaylist', true);

      this.triggerSegmentEvents();

      if (this.clItemData.all_caught_up) {
        this.$router.push(`/listen/${this.clItemData.season.podcast.publisher.slug}/${this.clItemData.season.podcast.slug}/${this.clItemData.season.podcast.uuid}`);
      } else if (this.currentProgress >= 95 && !this.clItemData.all_caught_up) {
        this.fetchAndPlayNextEpisode();
      } else {
        const { episode, play } = this.$store.getters;
        if (!play || (episode.uuid !== this.clItemData.uuid)) {
          this.loadMediaInPlayer(this.clItemData.media_url, this.clItemData.is_exclusive);
        }
        this.$store.dispatch('setContinuousListeningActivePodcast', this.clItemData.season.podcast.uuid);
        this.play(this.clItemData);
      }
    },
    redirectTo(link) {
      this.trackSegmentEvent();
      this.$router.push(link);
    },
    setShowTempTitle(play) {
      const { episode } = this.$store.getters;
      if (episode.uuid === this.clItemData.uuid) {
        // if (this.episode.temp_title !== 'All Caught Up') {
        this.show_temp_title = !play;
        this.clItemData.show_temp_title = !play;
        // }
      }
    },
    setTempTitle() {
      const progress = this.$store.getters.episodeProgressByUuid(this.episode.uuid);
      const { episode, nextEpisode } = this.$store.getters;
      // This condition will display all caught up on  app start from CL bucket data
      if (this.clItemData && !this.clItemData.uuid && this.clItemData.all_caught_up) {
        this.$set(this.clItemData, 'temp_title', 'All Caught Up');
        this.$set(this.clItemData, 'all_caught_up', !this.playing);
      }
      if (progress && progress.progress_percent) {
        if (progress.progress_percent < 95) {
          this.$set(this.clItemData, 'temp_title', 'Resume Episode');
        } else if (episode.uuid === this.episode.uuid && nextEpisode && nextEpisode.season && nextEpisode.season.podcast.uuid === this.clItemData.season.podcast.uuid) {
          this.$set(this.clItemData, 'temp_title', 'Play Next Episode');
        } else if (episode.uuid !== this.episode.uuid && progress && progress.progress_percent >= 95) {
          this.$set(this.clItemData, 'temp_title', 'Play Next Episode');
        } else {
          // This condition is for scenario when episode is running.
          this.$set(this.clItemData, 'temp_title', 'All Caught Up');
        }
      }
    },
    trackSegmentEvent() {
      log.info('Segment Event Called');
      const data = {
        content_type: this.episode.item_type,
        podcast_name: this.episode.title,
        podcast_uuid: this.episode.uuid,
        publisher: this.episode.publisher ? this.episode.publisher.name : '',
        category: this.episode.categories ? this.episode.categories.map(item => item.name) : '',
      };
      if (this.bucket && this.bucket.uuid) {
        data.bucket_position = this.bucket.position;
        data.bucket_id = this.episode.bucket_uuid;
        data.bucket_type = this.bucket.bucket_type;
      }
      this.$analytics.track('content_select', data);
    },
    play(episode) {
      const currentEpisode = this.$store.getters.episode;
      const { playlistType } = this.$store.getters;
      if (currentEpisode && currentEpisode.uuid === episode.uuid && playlistType === 'continue-listening') {
        const { play } = this.$store.getters;
        if (play) {
          this.$router.push(`/listen/${this.clItemData.season.podcast.publisher.slug}/${this.clItemData.season.podcast.slug}/${this.clItemData.season.podcast.uuid}`);
        } else this.$store.dispatch('play');
      } else {
        this.$store.dispatch('pause');
        this.$store.dispatch('setAndPlayEpisode', episode);
      }
    },
    fetchAndPlayNextEpisode() {
      /* When current episodes is listened more than 95% then play next episode */
      this.playbackService.fetchNextEpisode(this.clItemData.uuid, 'continue-listening').then((nextEpisode) => {
        if (nextEpisode && nextEpisode.uuid) {
          this.loadMediaInPlayer(nextEpisode.media_url, nextEpisode.is_exclusive);
          this.$store.dispatch('setContinuousListeningActivePodcast', nextEpisode.season.podcast.uuid);
          this.playbackService.syncCLBucketWithPlayerLocally(nextEpisode);
          this.play(nextEpisode);
        }
      }).catch((error) => {
        log.info(error);
      });
    },
    triggerSegmentEvents() {
      const bucketData = { ...this.bucket };
      bucketData.items = [];

      const bucketItemData = { ...this.episode };
      bucketItemData.item_position = this.$vnode.key;

      this.playerEventProvider.triggerEpisodePlayEvent(this.episode, 'continue_listening');
      this.playerEventProvider.triggerContentSelectEvent(bucketData, bucketItemData);
    },
  },
};
</script>
<style lang="scss">
@import "../assets/scss/variables.scss";
@import "../assets/scss/mixins.scss";

.cl-item {
  height: 280px;
  margin-right:15px;
  width: 227px;
  display: block;
  cursor: pointer;

  .cl-item-image {
    height: 227px;
    border: 1px solid $xlight-gray;
    background: #eee;
    position: relative;

    .el-progress {
      position: absolute;
      bottom: 0;
      z-index: 10;
      width: 100%;
      left: -0.5px;
    }
  }

  .cl-item-content {

    .playable {
        p {
        text-transform: capitalize;
        // margin-top: 11px;
        margin-bottom: 0 !important;
        color: $black;
        font-family: Favorit;
        text-align: center;
        font-size: 14px;
        line-height: 18px;
        text-overflow: ellipsis;
        overflow: hidden;
        height: 36px;
      }
    }
    .el-progress {
      margin-top:0;
      .el-progress-bar {
         .el-progress-bar__outer {
            border-radius: 0 !important;
            background-color: $xlight-gray !important;
            .el-progress-bar__inner {
              border-radius: 0 !important;
            }
          }
      }
    }
  }
}

@media (max-width: 1024px) {
  .cl-item {
    height: 253px;
    width: 200px;

    .cl-item-image {
      height: 200px;
    }
  }
}

@media (max-width: 768px) {
  .cl-item {
    height: 234px;
    width: 181px;

    .cl-item-image {
      height: 181px;
    }
  }
}
@media (max-width: 375px) {
  .cl-item {
    height: 160px;
    width: 115px;
    .cl-item-image {
      height: 115px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// app/src/components/CLItem.vue